import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, AlertTriangle, Sparkles, ArrowLeft } from 'lucide-react';
import { api } from '../api/client';
import { Markdown } from '../lib/markdown';

type Row = {
  organization_id: string;
  name: string;
  inn: string | null;
  project_status: string | null;
  contract_active: boolean;
  contract_ends_at: string | null;
  days_to_expire: number | null;
  tickets_total: number;
  tickets_p1: number;
  tickets_p2: number;
  tickets_open: number;
  sla_breaches: number;
  bad_ratings: number;
  last_ticket_at: string | null;
  days_since_last: number | null;
  score: number;
  flags: string[];
};

type Resp = { period_days: number; items: Row[]; summary_markdown: string | null };

export default function RiskDigestPage() {
  const [data, setData] = useState<Resp | null>(null);
  const [period, setPeriod] = useState(30);
  const [topN, setTopN] = useState(20);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async (withAi = false) => {
    if (withAi) setAiLoading(true); else setLoading(true);
    try {
      const r = await api.get('/api/v1/risk-digest', {
        params: { period_days: period, top_n: topN, with_ai: withAi },
      });
      setData(r.data);
    } finally {
      setLoading(false); setAiLoading(false);
    }
  };
  useEffect(() => { void load(false); /* eslint-disable-next-line */ }, [period, topN]);

  const scoreColor = (s: number) => s >= 30 ? 'text-red-300 bg-red-900/40 border-red-700'
    : s >= 15 ? 'text-orange-300 bg-orange-900/40 border-orange-700'
    : 'text-yellow-300 bg-yellow-900/30 border-yellow-700';

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/dashboard" className="hover:text-blue-400 inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Дашборд
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2 mt-1">
            <AlertTriangle className="text-orange-400" size={22} /> Контракты под риском
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Эвристика: P1/P2 тикеты, SLA-нарушения, плохие оценки, истекающий договор, пилот, тишина 90+ дней.
            Кликни «AI-обзор» — DeepSeek сформирует короткий разбор.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs text-slate-400">
            Период
            <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                    value={period} onChange={e => setPeriod(Number(e.target.value))}>
              <option value={7}>7 дней</option>
              <option value={30}>30 дней</option>
              <option value={90}>90 дней</option>
              <option value={180}>180 дней</option>
              <option value={365}>год</option>
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Топ
            <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                    value={topN} onChange={e => setTopN(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button onClick={() => void load(true)} disabled={aiLoading || !data?.items.length}
                  className="px-3 py-1.5 rounded bg-purple-700 hover:bg-purple-600 disabled:bg-slate-700 text-white text-xs font-bold inline-flex items-center gap-1.5">
            {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            AI-обзор
          </button>
        </div>
      </div>

      {/* AI summary */}
      {data?.summary_markdown && (
        <div className="bg-purple-950/30 border border-purple-900/60 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-wider text-purple-400 font-bold mb-2 inline-flex items-center gap-1">
            <Sparkles size={11} /> AI-обзор
          </div>
          <Markdown source={data.summary_markdown} />
        </div>
      )}

      {/* Таблица */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-slate-500 text-sm py-8 text-center">
            <Loader2 className="inline animate-spin mr-2" size={14} />считаем…
          </div>
        ) : !data?.items.length ? (
          <div className="text-slate-500 text-sm py-8 text-center">За период риска не обнаружено</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-3 font-bold">Организация</th>
                <th className="text-right py-2 px-2 font-bold">Score</th>
                <th className="text-left py-2 px-2 font-bold">Статус</th>
                <th className="text-left py-2 px-2 font-bold">Договор</th>
                <th className="text-right py-2 px-2 font-bold">Тикетов</th>
                <th className="text-right py-2 px-2 font-bold">P1/P2</th>
                <th className="text-right py-2 px-2 font-bold">SLA</th>
                <th className="text-right py-2 px-2 font-bold">Оценок ≤3</th>
                <th className="text-left py-2 px-3 font-bold">Признаки</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(r => (
                <tr key={r.organization_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-2 px-3">
                    <Link to={`/organizations/${r.organization_id}`} className="text-blue-400 hover:text-blue-300 font-bold">
                      {r.name}
                    </Link>
                    {r.inn && <div className="text-[10px] text-slate-500 font-mono">ИНН {r.inn}</div>}
                  </td>
                  <td className="py-2 px-2 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold border ${scoreColor(r.score)}`}>
                      {r.score}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-slate-300">
                    {r.project_status === 'pilot' ? 'Пилот'
                      : r.project_status === 'support' ? 'Поддержка'
                      : r.project_status === 'no_support' ? 'Без поддержки' : '—'}
                  </td>
                  <td className="py-2 px-2 text-slate-300">
                    {r.days_to_expire === null ? (r.contract_active ? 'активен' : 'не активен')
                      : r.days_to_expire < 0 ? <span className="text-red-300">истёк {-r.days_to_expire}д</span>
                      : r.days_to_expire <= 30 ? <span className="text-orange-300">~{r.days_to_expire}д</span>
                      : <span className="text-slate-400">~{r.days_to_expire}д</span>}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {r.tickets_total}
                    <div className="text-[10px] text-slate-500">откр {r.tickets_open}</div>
                  </td>
                  <td className="py-2 px-2 text-right">
                    {r.tickets_p1 > 0 && <span className="text-red-300 font-bold">{r.tickets_p1}</span>}
                    {r.tickets_p1 > 0 && r.tickets_p2 > 0 && <span className="text-slate-600 mx-1">/</span>}
                    {r.tickets_p2 > 0 && <span className="text-orange-300 font-bold">{r.tickets_p2}</span>}
                    {r.tickets_p1 === 0 && r.tickets_p2 === 0 && <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {r.sla_breaches > 0
                      ? <span className="text-red-300 font-bold">{r.sla_breaches}</span>
                      : <span className="text-slate-600">0</span>}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {r.bad_ratings > 0
                      ? <span className="text-orange-300 font-bold">{r.bad_ratings}</span>
                      : <span className="text-slate-600">0</span>}
                  </td>
                  <td className="py-2 px-3 text-[11px] text-slate-400 max-w-md">
                    {r.flags.join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
